import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FeedToPlantTonnageChart from '../components/FeedToPlantTonnageChart';
import MultiLineChart from '../components/charts/MultiLineChart';
import SimpleBarChart from '../components/charts/SimpleBarChart';
import sourceColors from '../sourceColors.json';
import { getDisplayName } from '../sourceDisplayNames';
import { CalendarIcon } from '@heroicons/react/solid';
import DateRangePicker from '../components/DateRangePicker';

const sites = Object.keys(sourceColors).filter(k => k !== 'targets' && k !== 'productCvTargets');
const sourceTargets = sourceColors.targets || {};
const productCvTargets = sourceColors.productCvTargets || {};

function LiveDashboard() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState([...sites]);
  const [startDate, setStartDate] = useState(() => {
    const end = new Date();
    return new Date(end.getTime() - 24 * 60 * 60 * 1000);
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [rangeLabel, setRangeLabel] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const defaultStart = useRef(startDate);
  const defaultEnd = useRef(endDate);
  const [showDefaultBtn, setShowDefaultBtn] = useState(false);
  const [fadeDefaultBtn, setFadeDefaultBtn] = useState(false);
  const [activeShift, setActiveShift] = useState(0);
  const [allChartData, setAllChartData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [feedCVData, setFeedCVData] = useState([]);
  const [productCVData, setProductCVData] = useState([]);
  const [productTonnageData, setProductTonnageData] = useState([]);
  const [productTonnageMax, setProductTonnageMax] = useState(0);
  const [productTonnageTicks, setProductTonnageTicks] = useState([]);
  const [overallYield, setOverallYield] = useState(0);
  const [cvLabels, setCvLabels] = useState([]);
  const [plantStats, setPlantStats] = useState({});
  const [consumptionStats, setConsumptionStats] = useState({});
  const [showRound, setShowRound] = useState(false);
  const [feedTotal, setFeedTotal] = useState(0);
  const [productTonnageTotal, setProductTonnageTotal] = useState(0);

  const toDateTimeStr = d => {
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset)
      .toISOString()
      .slice(0, 19)
      .replace('T', ' ');
  };

  const toPickerValue = d => {
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 19);
  };

  const parsePickerValue = value => {
    if (!value) return null;
    const normalized = value.length === 16 ? `${value}:00` : value;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const startStr = useMemo(() => toDateTimeStr(startDate), [startDate]);
  const endStr = useMemo(() => toDateTimeStr(endDate), [endDate]);

  const formatTime = ts => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const resetToDefault = () => {
    const end = new Date();
    const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    defaultStart.current = start;
    defaultEnd.current = end;
    setStartDate(start);
    setEndDate(end);
    setActiveShift(0);
  };

  useEffect(() => {
    const isDefault =
      startDate.getTime() === defaultStart.current.getTime() &&
      endDate.getTime() === defaultEnd.current.getTime();
    if (!isDefault) {
      setShowDefaultBtn(true);
    } else if (showDefaultBtn) {
      setFadeDefaultBtn(true);
      const t = setTimeout(() => {
        setShowDefaultBtn(false);
        setFadeDefaultBtn(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [startDate, endDate, showDefaultBtn]);

  useEffect(() => {
    const f = d =>
      d.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    setRangeLabel(`${f(startDate)} - ${f(endDate)}`);
  }, [startDate, endDate]);

  useEffect(() => {
    console.log('Fetching chart data for range', startStr, endStr);
    fetch(
      `/api/chart-data.php?start=${encodeURIComponent(startStr)}&end=${encodeURIComponent(endStr)}`
    )
      .then(res => res.json())
      .then(res => {
        console.log('chart-data response', res);
        setAllChartData(res.data || []);
      })
      .catch(err => {
        console.log('chart-data error', err);
        setAllChartData([]);
      });
  }, [startStr, endStr]);

  useEffect(() => {
    setChartData(allChartData.filter(r => selected.includes(r.source)));
  }, [allChartData, selected]);

  useEffect(() => {
    console.log('chartData updated', chartData);
  }, [chartData]);

  useEffect(() => {
    console.log('Fetching plant stats for range', startStr, endStr);
    fetch(
      `/api/plant-stats.php?start=${encodeURIComponent(startStr)}&end=${encodeURIComponent(endStr)}`
    )
      .then(res => res.json())
      .then(res => {
        console.log('plant stats response', res);
        setPlantStats(res || {});
      })
      .catch(err => {
        console.log('plant stats error', err);
        setPlantStats({});
      });
  }, [startStr, endStr]);

  useEffect(() => {
    console.log('Fetching feed total for range', startStr, endStr);
    fetch(
      `/api/feed-total.php?start=${encodeURIComponent(startStr)}&end=${encodeURIComponent(endStr)}`
    )
      .then(res => res.json())
      .then(res => {
        setFeedTotal(res.total_feed || 0);
      })
      .catch(err => {
        console.log('feed total error', err);
        setFeedTotal(0);
      });
  }, [startStr, endStr]);

  useEffect(() => {
    if (!selected.length) {
      setOverallYield(0);
      return;
    }
    console.log('Fetching yield for range', startStr, endStr, 'sources', selected);
    const srcParam = selected.map(encodeURIComponent).join(',');
    fetch(
      `/api/overall-yield.php?start=${encodeURIComponent(startStr)}&end=${encodeURIComponent(endStr)}&sources=${srcParam}`
    )
      .then(res => res.json())
      .then(res => {
        console.log('yield response', res);
        setOverallYield(res.yield_percent || 0);
      })
      .catch(err => {
        console.log('yield error', err);
        setOverallYield(0);
      });
  }, [startStr, endStr, selected]);

  useEffect(() => {
    if (!selected.length) {
      setConsumptionStats({});
      return;
    }
    console.log('Fetching consumption stats for range', startStr, endStr, 'sources', selected);
    const srcParam = selected.map(encodeURIComponent).join(',');
    fetch(
      `/api/consumption-stats.php?start=${encodeURIComponent(startStr)}&end=${encodeURIComponent(endStr)}&sources=${srcParam}`
    )
      .then(res => res.json())
      .then(res => {
        console.log('consumption stats response', res);
        setConsumptionStats(res || {});
      })
      .catch(err => {
        console.log('consumption stats error', err);
        setConsumptionStats({});
      });
  }, [startStr, endStr, selected]);

  useEffect(() => {
    if (!selected.length) {
      setFeedCVData([]);
      setProductCVData([]);
      setProductTonnageData([]);
      setProductTonnageMax(0);
      setProductTonnageTicks([]);
      setCvLabels([]);
      setProductTonnageTotal(0);
      return;
    }
    console.log('Fetching plant report data for range', startStr, endStr, 'sources', selected);
    const srcParam = selected.map(encodeURIComponent).join(',');
    fetch(
      `/api/plant-report-data.php?start=${encodeURIComponent(startStr)}&end=${encodeURIComponent(endStr)}&sources=${srcParam}`
    )
      .then(res => res.json())
      .then(res => {
        console.log('plant-report response', res);
        const rows = res.data || [];
        const labels = Array.from(new Set(rows.map(r => r.timestamp))).sort();
        setCvLabels(labels.map(formatTime));
        const labelIndexMap = new Map(labels.map((label, index) => [label, index]));

        const createSeries = (label, defaultColor) => ({
          label,
          color: defaultColor,
          data: new Array(labels.length).fill(null),
          segmentColors: new Array(labels.length).fill(null),
          pointBackgroundColors: new Array(labels.length).fill(null),
          pointBorderColors: new Array(labels.length).fill(null),
          sources: new Array(labels.length).fill(null),
          pointRadius: 3,
          pointHoverRadius: 5,
        });

        const feedSeries = createSeries('Feed CV', '#22c55e');
        const productSeries = createSeries('Product CV', '#3b82f6');
        const tonRows = [];
        let totalProductTons = 0;
        rows.forEach(r => {
          const idx = labelIndexMap.get(r.timestamp);
          if (idx !== undefined) {
            if (r.feed_cv > 0) {
              const color = sourceColors[r.source] || '#ffffff';
              feedSeries.data[idx] = r.feed_cv;
              feedSeries.segmentColors[idx] = color;
              feedSeries.pointBackgroundColors[idx] = color;
              feedSeries.pointBorderColors[idx] = color;
              feedSeries.sources[idx] = r.source;
            }
            if (r.dms_product_cv > 0) {
              const color = sourceColors[r.source] || '#ffffff';
              productSeries.data[idx] = r.dms_product_cv;
              productSeries.segmentColors[idx] = color;
              productSeries.pointBackgroundColors[idx] = color;
              productSeries.pointBorderColors[idx] = color;
              productSeries.sources[idx] = r.source;
            }
          }
          if (r.product_tons > 0) {
            tonRows.push({
              label: formatTime(r.timestamp),
              value: r.product_tons,
              color: sourceColors[r.source],
              display: Math.trunc(r.product_tons).toLocaleString(),
              valueInside: true,
              tooltip: getDisplayName(r.source),
            });
            totalProductTons += r.product_tons;
          }
        });
        const tonBarData = tonRows;
        const calcStep = val => {
          if (val === 0) return 1;
          const rough = val / 5;
          const pow = Math.pow(10, Math.floor(Math.log10(rough)));
          const mults = [1, 2, 5, 10];
          for (const m of mults) {
            if (rough <= m * pow) return m * pow;
          }
          return 10 * pow;
        };
        const maxVal = Math.max(...tonBarData.map(d => d.value), 0);
        const step = calcStep(maxVal);
        const tickCount = Math.ceil(maxVal / step);
        const ticks = Array.from({ length: tickCount + 1 }, (_, i) => +(i * step).toFixed(2));
        const maxAxis = tickCount > 0 ? ticks[ticks.length - 1] : step;
        const feedHasData = feedSeries.data.some(v => v != null);
        const productHasData = productSeries.data.some(v => v != null);

        setFeedCVData(feedHasData ? [feedSeries] : []);
        setProductCVData(productHasData ? [productSeries] : []);
        setProductTonnageData(tonBarData);
        setProductTonnageMax(maxAxis);
        setProductTonnageTicks(ticks);
        setProductTonnageTotal(totalProductTons);
      })
      .catch(() => {
        console.log('plant-report error');
        setFeedCVData([]);
        setProductCVData([]);
        setProductTonnageData([]);
        setProductTonnageMax(0);
        setProductTonnageTicks([]);
        setCvLabels([]);
        setProductTonnageTotal(0);
      });
  }, [startStr, endStr, selected]);

  useEffect(() => {
    console.log('cvLabels updated', cvLabels);
    console.log('feedCVData', feedCVData);
    console.log('productCVData', productCVData);
    console.log('productTonnageData', productTonnageData);
    console.log('plantStats', plantStats);
    console.log('consumptionStats', consumptionStats);
  }, [cvLabels, feedCVData, productCVData, productTonnageData, plantStats, consumptionStats]);

  const toggle = site => {
    setSelected(s =>
      s.includes(site) ? s.filter(x => x !== site) : [...s, site]
    );
  };

  const toggleAll = () => {
    setSelected(s => (s.length === sites.length ? [] : [...sites]));
  };

  useEffect(() => {
    console.log('Selected sources', selected);
  }, [selected]);

  const toggleShift = shift => {
    if (activeShift === shift) {
      resetToDefault();
      return;
    }
    setActiveShift(shift);
    const now = new Date();
    if (shift === 1) {
      const start = new Date(now);
      start.setHours(6, 0, 0, 0);
      const end = new Date(now);
      end.setHours(18, 0, 0, 0);
      setStartDate(start);
      setEndDate(end);
    } else if (shift === 2) {
      const end = new Date(now);
      end.setHours(6, 0, 0, 0);
      const start = new Date(end);
      start.setDate(start.getDate() - 1);
      start.setHours(18, 0, 0, 0);
      setStartDate(start);
      setEndDate(end);
    }
  };

  const prevDay = () => {
    const step = 24 * 60 * 60 * 1000;
    setStartDate(new Date(startDate.getTime() - step));
    setEndDate(new Date(endDate.getTime() - step));
  };
  const nextDay = () => {
    const step = 24 * 60 * 60 * 1000;
    const now = new Date();
    const newEnd = new Date(endDate.getTime() + step);
    if (newEnd <= now) {
      setStartDate(new Date(startDate.getTime() + step));
      setEndDate(newEnd);
    }
  };
  const disableNext = endDate.getTime() >= new Date().getTime();

  const magnetiteValue = consumptionStats.magnetite_consumption || 0;
  const magnetiteMax = Math.max(magnetiteValue, 0.5) * 1.1;
  const magnetiteTicks = Array.from({ length: 5 }, (_, i) => i * (magnetiteMax / 4));

  const createCvTooltip = defaultLabel => ({ dataset, dataIndex }) => {
    const value = dataset?.data?.[dataIndex];
    if (value == null) return '';
    const numeric = Number(value);
    const formatted = Number.isFinite(numeric) ? numeric.toFixed(2) : value;
    const sourceKey = dataset?.sources?.[dataIndex];
    const label = sourceKey ? getDisplayName(sourceKey) : defaultLabel;
    return `${label}: ${formatted} MJ/kg`;
  };

  return (
    <div className="p-4 space-y-4 text-white flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-x-10 gap-y-2 border-b border-gray-600 pb-2">
        {sites.map(site => (
          <div
            key={site}
            onClick={() => toggle(site)}
            className="cursor-pointer px-2"
            style={{
              borderBottom: selected.includes(site)
                ? `2px solid ${sourceColors[site]}`
                : 'none',
            }}
          >
            {getDisplayName(site)}
          </div>
        ))}
        <label className="flex items-center space-x-1">
          <input
            type="checkbox"
            className="form-checkbox h-4 w-4 rounded-full text-[#22c55e] bg-[#0d2c3d] border-gray-600 focus:ring-[#22c55e]"
            checked={selected.length === sites.length}
            onChange={toggleAll}
          />
          <span className="text-sm font-bold text-[#22c55e]">Select All</span>
        </label>
        <div className="flex items-center space-x-2 ml-auto">
          {showDefaultBtn && (
            <button
              onClick={resetToDefault}
              className={`px-2 py-1 bg-[#00D04C] rounded shadow hover:bg-[#00B240] transition ease-in-out duration-200 transition-opacity ${fadeDefaultBtn ? 'opacity-0' : 'opacity-100'}`}
            >
              Default
            </button>
          )}
          <button
            onClick={() => toggleShift(1)}
            className={`px-2 py-1 rounded shadow transition ease-in-out duration-200 ${
              activeShift === 1
                ? 'bg-[#123b52] shadow-inner'
                : 'bg-[#0d2c3d] hover:bg-[#123b52]'
            }`}
          >
            Shift 1
          </button>
          <button
            onClick={() => toggleShift(2)}
            className={`px-2 py-1 rounded shadow transition ease-in-out duration-200 ${
              activeShift === 2
                ? 'bg-[#123b52] shadow-inner'
                : 'bg-[#0d2c3d] hover:bg-[#123b52]'
            }`}
          >
            Shift 2
          </button>
          <button
            onClick={prevDay}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] transition ease-in-out duration-200"
            aria-label="Previous Day"
          >
            {'<'}
          </button>
          <div
            className="px-3 py-1 bg-[#0d2c3d] rounded shadow flex items-center space-x-1 cursor-pointer"
            onClick={() => setShowPicker(true)}
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="text-sm">{rangeLabel}</span>
          </div>
          <button
            onClick={nextDay}
            disabled={disableNext}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] disabled:opacity-50 transition ease-in-out duration-200"
            aria-label="Next Day"
          >
            {'>'}
          </button>
        </div>
      </div>

      {showPicker && (
        <DateRangePicker
          initialStart={toPickerValue(startDate)}
          initialEnd={toPickerValue(endDate)}
          onApply={(s, e) => {
            const start = parsePickerValue(s);
            const end = parsePickerValue(e);
            if (!start || !end) return;
            setStartDate(start);
            setEndDate(end);
            setActiveShift(0);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}

      <div className="grid flex-1 grid-cols-4 grid-rows-3 gap-4">
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <h2 className="mb-2 text-center text-lg">Yield</h2>
          <SimpleBarChart
            height="100%"
            data={[
              {
                label: 'Yield %',
                value: overallYield,
                color: '#22c55e',
                display: `${overallYield.toFixed(2)}%`,
                valueInside: true,
              },
            ]}
            max={100}
            ticks={[0, 20, 40, 60, 80, 100]}
            valueClass="text-sm font-bold"
            labelFontSize={14}
            labelBold
          />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <h2 className="mb-2 text-center text-sm">Plant Stats</h2>
          <SimpleBarChart
            height="100%"
            data={[
              {
                label: 'Plant Utilisation',
                value: plantStats.plant_utilisation || 0,
                color: '#22c55e',
                display: `${Math.round(plantStats.plant_utilisation || 0)}%`,
                valueInside: true,
              },
              {
                label: 'Avail. Engineering',
                value: plantStats.availability || 0,
                color: '#3b82f6',
                display: `${Math.round(plantStats.availability || 0)}%`,
                valueInside: true,
              },
              {
                label: 'Avail. Process',
                value: plantStats.availability_process || 0,
                color: '#9ca3af',
                display: `${Math.round(plantStats.availability_process || 0)}%`,
                valueInside: true,
              },
            ]}
            max={100}
            ticks={[0, 20, 40, 60, 80, 100]}
            valueClass="text-sm font-bold"
            labelFontSize={14}
            labelBold
          />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-center text-sm flex-1">Magnetite Consumption</h2>
            <button
              className="text-xs bg-blue-600 rounded px-2 py-1"
              onClick={() => navigate('/dev-test')}
            >
              View
            </button>
          </div>
          <SimpleBarChart
            height="100%"
            data={[
              {
                label: 'Consumption (kg/ton)',
                value: consumptionStats.magnetite_consumption || 0,
                color: '#22c55e',
                display:
                  consumptionStats.magnetite_consumption !== undefined
                    ? (consumptionStats.magnetite_consumption || 0).toFixed(2)
                    : '0',
                tooltip: 'Magnetite Consumption per ton of feed',
                valueInside: true,
              },
              {
                label: 'Target',
                value: 0.5,
                color: '#3b82f6',
                display: '0.5',
                valueInside: true,
              },
            ]}
            max={magnetiteMax}
            ticks={magnetiteTicks}
            valueClass="text-sm font-bold"
            labelFontSize={14}
            labelBold
          />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-center text-sm flex-1">Flocculant Consumption</h2>
            <button
              className="text-xs bg-blue-600 rounded px-2 py-1"
              onClick={() => navigate('/dev-test')}
            >
              View
            </button>
          </div>
          <SimpleBarChart
            height="100%"
            data={[
              {
                label: 'Consumption (g/ton)',
                value: consumptionStats.flocculant_consumption || 0,
                color: '#22c55e',
                display:
                  consumptionStats.flocculant_consumption !== undefined
                    ? (consumptionStats.flocculant_consumption || 0).toFixed(2)
                    : '0',
                tooltip: 'Flocculant Consumption per ton of feed',
                valueInside: true,
              },
              {
                label: 'Target',
                value: 10,
                color: '#3b82f6',
                display: '10',
                valueInside: true,
              },
            ]}
            max={Math.max(
              consumptionStats.flocculant_consumption || 0,
              10,
              5
            )}
            ticks={(() => {
              const mx = Math.max(
                consumptionStats.flocculant_consumption || 0,
                10,
                5
              );
              return Array.from({ length: Math.ceil(mx / 5) + 1 }, (_, i) => i * 5);
            })()}
            valueClass="text-sm font-bold"
            labelFontSize={14}
            labelBold
          />
        </div>
        <div className="col-span-2 bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <div className="mb-2 relative flex items-center justify-center">
            <span className="absolute left-0 text-sm">
              Total: {feedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <h2 className="text-lg">Feed to Plant</h2>
            <label className="absolute right-0 flex items-center space-x-1 text-xs">
              <input
                type="checkbox"
                className="form-checkbox h-3 w-3 text-[#22c55e] bg-[#0d2c3d] border-gray-600 focus:ring-[#22c55e]"
                checked={showRound}
                onChange={e => setShowRound(e.target.checked)}
              />
              <span>Target</span>
            </label>
          </div>
          <div className="mb-2 flex flex-wrap justify-center gap-2 text-xs">
            {sites.map(s => (
              <div key={s} className="flex items-center space-x-1">
                <span
                  className="w-3 h-3 rounded-full inline-block"
                  style={{ backgroundColor: sourceColors[s] }}
                />
                <span>{getDisplayName(s)}</span>
              </div>
            ))}
          </div>
          <FeedToPlantTonnageChart
            data={chartData}
            height="100%"
            targetLines={
              showRound
                ? [
                    { label: 'Other', value: 120, color: '#ffffff' },
                    {
                      label: 'Rewash Coal',
                      value: 100,
                      color: sourceColors['Rewash Coal'],
                    },
                  ]
                : []
            }
          />
        </div>
        <div className="col-span-2 bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <h2 className="mb-2 text-center text-lg">Feed CV</h2>
          <MultiLineChart
            datasets={feedCVData}
            labels={cvLabels}
            yLabel="Feed CV (MJ/kg)"
            height="100%"
            beginAtZero
            tooltipLabelCallback={createCvTooltip('Feed CV')}
          />
        </div>
        <div className="col-span-2 bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <div className="mb-2 relative flex items-center justify-center">
            <span className="absolute left-0 text-sm">
              Total: {productTonnageTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <h2 className="text-lg">Product Tonnage</h2>
          </div>
          {productTonnageData.length > 0 ? (
            <SimpleBarChart
              height="100%"
              data={productTonnageData}
              max={productTonnageMax}
              ticks={productTonnageTicks}
              valueClass="text-sm font-bold"
              labelFontSize={14}
              labelBold
              showValueLabels={productTonnageData.length <= 12}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm">No data</div>
          )}
        </div>
        <div className="col-span-2 bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-lg flex-1 text-center">Product CV</h2>
            <label className="flex items-center space-x-1 text-xs">
              <input
                type="checkbox"
                className="form-checkbox h-3 w-3 text-[#22c55e] bg-[#0d2c3d] border-gray-600 focus:ring-[#22c55e]"
                checked={showRound}
                onChange={e => setShowRound(e.target.checked)}
              />
              <span>Target</span>
            </label>
          </div>
          <MultiLineChart
            datasets={productCVData}
            labels={cvLabels}
            yLabel="Product CV (MJ/kg)"
            height="100%"
            beginAtZero
            targetLines={
              showRound
                ? selected
                    .map(s =>
                      productCvTargets[s]
                        ? { value: productCvTargets[s], color: sourceColors[s] }
                        : null
                    )
                    .filter(Boolean)
                : []
            }
            tooltipLabelCallback={createCvTooltip('Product CV')}
          />
        </div>
      </div>
    </div>
  );
}

export default LiveDashboard;
